import { connectNumericMenu } from 'instantsearch.js/es/connectors';

export const IN_STOCK_ATTRIBUTE = 'inventory_quantity';
export const IN_STOCK_VALUE = '1:';

/** Only native watch collections on the existing storefront hosts opt in. */
export function stockFilterEnabled(
  document: Pick<Document, 'querySelector'>,
  location: Pick<Location, 'hostname' | 'pathname'>,
): boolean {
  if (![
    'www.raffi-jewellers.ca',
    'raffi-jewellers.ca',
    'raffi-internal-mod-dev-staging-f924c54ebe58.herokuapp.com',
  ].includes(location.hostname)) return false;
  let pathname: string;
  try { pathname = decodeURIComponent(location.pathname); } catch (_) { return false; }
  return /^\/(?:fr\/)?collections\/timepieces(?:\/[a-z0-9-]+)*\/?$/i.test(pathname)
    && !/rolex|tag[\s_-]*heuer/i.test(pathname)
    && !!document.querySelector('main[data-watch-buying="collection"]');
}

/** The numeric-menu UI state for positive physical inventory. */
export function inStockRouteEnabled(value: unknown): boolean {
  return value === IN_STOCK_VALUE;
}

/**
 * A new explicit stock choice takes precedence over an old "Any location" URL.
 * Never coerce URL strings: "false", arrays and arbitrary ranges must not enable it.
 */
export function inStockRouteValue(routeState: Record<string, unknown>): typeof IN_STOCK_VALUE | undefined {
  if (!routeState || typeof routeState !== 'object' || Array.isArray(routeState)) return undefined;
  if (Object.prototype.hasOwnProperty.call(routeState, 'inStock')) {
    return routeState.inStock === true || routeState.inStock === 'true'
      ? IN_STOCK_VALUE
      : undefined;
  }
  return Object.prototype.hasOwnProperty.call(routeState, IN_STOCK_ATTRIBUTE)
    && inStockRouteEnabled(routeState[IN_STOCK_ATTRIBUTE])
    ? IN_STOCK_VALUE
    : undefined;
}

type StockMenuItem = { value: string; isRefined: boolean };
type StockMenuRenderOptions = {
  items: StockMenuItem[];
  refine: (value: string) => void;
};

/**
 * Positive inventory is independent of the commercial "available online" tag.
 * Mount instead of the old inventory_quantity location widget, never alongside it.
 * Specific store menus use other attributes and can remain active with this filter.
 */
export default function inStockNumericMenu(params: {
  container: string | HTMLElement;
  label: string;
}) {
  let root: HTMLElement | null = null;
  let checkbox: HTMLInputElement | null = null;
  let currentRefine: StockMenuRenderOptions['refine'] | null = null;
  let selectedValue: string | null = null;
  let resetPage: (() => void) | null = null;

  const render = ({ items, refine }: StockMenuRenderOptions) => {
    const container = typeof params.container === 'string'
      ? document.querySelector<HTMLElement>(params.container)
      : params.container;
    if (!container) return;

    // Keep the input through result updates so keyboard focus and sheet tab order survive.
    if (!root || root.parentElement !== container || !checkbox) {
      root?.remove();
      root = document.createElement('div');
      root.className = 'ais-ToggleRefinement';
      const label = document.createElement('label');
      label.className = 'ais-ToggleRefinement-label';
      checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.className = 'ais-ToggleRefinement-checkbox';
      const text = document.createElement('span');
      text.className = 'ais-ToggleRefinement-labelText';
      text.textContent = params.label;
      label.append(checkbox, text);
      root.append(label);
      container.append(root);
      checkbox.addEventListener('change', () => {
        if (!checkbox || !currentRefine || selectedValue === null) return;
        resetPage?.();
        currentRefine(checkbox.checked ? selectedValue : encodeURIComponent('{}'));
      });
    }

    const item = items[0];
    currentRefine = refine;
    selectedValue = item?.value ?? null;
    checkbox.checked = item?.isRefined === true;
    checkbox.disabled = !item;
  };

  const unmount = () => {
    root?.remove();
    root = null;
    checkbox = null;
    currentRefine = null;
    selectedValue = null;
    resetPage = null;
  };

  const widget = connectNumericMenu(render, unmount)({
    attribute: IN_STOCK_ATTRIBUTE,
    items: [{ label: params.label, start: 1 }],
  });
  const init = widget.init!;
  widget.init = function(options) {
    // The numeric-menu connector resets page when applying bounds, but not
    // when clearing its empty option. Both checkbox actions must start at page 1.
    resetPage = () => { options.helper.setPage(0); };
    return init.call(this, options);
  };
  return widget;
}
